import { Action as ReduxAction } from 'redux';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { EntityActionNames, IEntityAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { ContentRelayUtils } from '../utils/ContentRelayUtils';
import { deleteRelayPlans } from '../utils/relayPlanActions';

interface DeleteRelayPlanParams {
  luids: string[];
}

/**
 * Content Relays → Actions → "Delete" on one or more plans. Confirms (destructive), then deletes via
 * the content-mobility `deletePlan` web method, refreshes the space, and toasts success. Gated by the
 * same flag + site-setting check as the rest of Content Relay.
 *
 * Opens the confirmation dialog imperatively (new code avoids Redux/Saga) and returns a no-op action,
 * since the Actions menu dispatches whatever createReduxAction returns.
 */
export const DeleteContentRelayPlanEntityAction: IEntityAction<IContentInfo, {}> = {
  name: EntityActionNames.DeleteContentRelayPlan,
  translateName: Messages.delete_action,
  isVisible: (targetItems: IContentInfo[]) =>
    ContentRelayUtils.isContentRelayEnabled() && targetItems.length > 0,
  isClickable: () => true,
  createReduxAction: (targetItems: IContentInfo[]): ReduxAction => {
    const luids = targetItems.map(function(item) { return item.luid; });
    const count = luids.length;

    openReactConfirmationDialog<DeleteRelayPlanParams, void>({
      title: Messages.relayPlanDeleteTitle({ COUNT: count }),
      message: Messages.relayPlanDeleteConfirm({ COUNT: count }),
      actionName: Messages.delete_action(),
      isActionDelete: true,
      dialogWidth: TabDialogWidth.width375px,
      paramsForAction: { luids: luids },
      action: function(params: DeleteRelayPlanParams) {
        return deleteRelayPlans(params.luids).then(function() {
          // Re-fetch the current space so the deleted plan(s) drop out of the grid.
          getAppStore().dispatch(requestSpaceContents());
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.relayPlanDeletedToast({ COUNT: count }) });
        });
      },
      // The dialog's returned promise rejects when the user dismisses it — swallow it here since we
      // are not inside a saga (where the root saga would absorb it).
    }).result.catch(function() { return undefined; });

    return { type: 'contentRelay/deletePlanDialogRequested' };
  },
};
